import { KikGenericResponse } from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { useQuery } from "@tanstack/react-query";
import { createContext, ReactNode, useContext, useState } from "react";

import { HttpService } from "~/services/http";
import Logger from "~/services/logger/logger";
import { ApiConstants } from "~/shared/api/api-constants.class";
import Editorials from "~/types/editorials";

import { useServerSideProps } from "./server-side-props";

type CountryDialogContextType = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  countries: Editorials.CountriesModel;
};

const CountryDialogContext = createContext<CountryDialogContextType | undefined>(undefined);

export function CountryDialogProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const { serverSideProps } = useServerSideProps();
  const ssrCountries: Editorials.CountriesModel | null | undefined = serverSideProps?.countryDialog;

  const { data: countries } = useQuery({
    queryKey: ["countryDialog"],
    enabled: !ssrCountries,
    queryFn: async () => {
      try {
        const url = ApiConstants.endpointsMapiV1.ecoContentfulCountryDialog;
        const countryDialogPromise = await HttpService.$http<KikGenericResponse<Editorials.CountriesModel>>({ url });
        return countryDialogPromise.data;
      } catch (e) {
        Logger.instance.error("Failed to load country dialog data", e);
        return undefined;
      }
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  return (
    <CountryDialogContext.Provider
      value={{
        isOpen,
        open,
        close,
        countries: ssrCountries ?? countries ?? { continents: [] },
      }}
    >
      {children}
    </CountryDialogContext.Provider>
  );
}

export function useCountryDialog() {
  const context = useContext(CountryDialogContext);

  if (!context) {
    throw new Error("useCountryDialog must be used inside <CountryDialogProvider>");
  }

  return context;
}
